<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  type: {
    type: String,
    default: 'text',
  },
  size: {
    type: String,
    default: 'md',
  },
  label: {
    type: String,
  },
  placeholder: {
    type: String,
  },
  maxlength: {
    type: Number,
    default: 524288
  },
  helper: {
    type: String,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  isError: {
    type: Boolean,
    default: false,
  },
  customClasses: {
    type: String,
  },
  inputClasses: {
    type: String,
  },
  id: {
    type: String,
  },
  // Soft-keyboard action label (e.g. "search") — used by mobile browsers.
  enterkeyhint: {
    type: String,
    default: undefined,
  },
  modelValue: {
    default: ''
  },
})


const emit = defineEmits({
  // falsy payloads ('', 0) are valid; returning the value itself made Vue warn
  'update:modelValue': () => true,
  focus: (e) => true,
  blur: (e) => true,
  keydown: (e) => true,
})

const computedValue = computed({
  get: () => props.modelValue,
  set: value => {
    emit('update:modelValue', value)
  },
})

const inputClass = computed(() => {
  const base = [
    'w-full leading-body bg-surface-3 focus:bg-surface-4 focus:outline-0 focus:ring-0 rounded transition-color placeholder-sureface-8 text-surface-text-2 disabled:text-surface-7 focus-visible:outline-none',
  ]
  if (props.isError) {
    base.push('ring-1 ring-error-light focus:ring-error-light bg-error-transparent')
  } else {
    base.push('border-neutral-n50 focus:border-surfaces-primary focus:placeholder:text-transparent')
  }
  if (props.inputClasses) {
    base.push(props.inputClasses)
  }
  // Native date / time pickers draw their own indicator; keep it legible on the dark surface.
  if (['date', 'time', 'datetime-local', 'month', 'week'].includes(props.type)) {
    base.push('[color-scheme:dark]')
  }
  // 16px typed text on mobile (avoids iOS Safari focus zoom); placeholder stays 13px.
  const size = props.size === 'lg'
    ? 'h-10'
    : props.size === 'sm'
      ? 'h-7 px-2 text-base md:text-tiny placeholder:text-tiny'
      : 'h-8 px-3 text-base md:text-tiny placeholder:text-tiny'
  
  base.push(size)
  return base
})

const wrapperClass = computed(() => {
  return props.helper ? 'pb-4' : ''
})

const focusState = ref(false)
const inputEl = ref(null)

defineExpose({
  focus: () => inputEl.value?.focus(),
  blur: () => inputEl.value?.blur(),
})

const onInputFocus = (e) => {
  focusState.value = true
  emit('focus', e)
}

const onInputBlur = (e) => {
  focusState.value = false
  emit('blur', e)
}

const helperClass = computed(() => {
  const classes = [
    'absolute text-xs px-3',
  ]
  classes.push(props.isError ? 'text-error' : 'text-surface-9')
  classes.push(props.label ? 'top-[72px]' : 'top-10')
  return classes
})
</script>

<template>
  <div class="relative w-full" :class="wrapperClass">
    <label class="block font-medium text-tiny text-surface-text-1 mb-2" v-if="label" :for="id">{{ label }}</label>
    <input
      ref="inputEl"
      :disabled="disabled"
      :readonly="readonly"
      :type="type"
      :class="[inputClass, customClasses]"
      :id="id"
      v-model="computedValue"
      :placeholder="placeholder"
      :maxlength="maxlength"
      :enterkeyhint="enterkeyhint"
      @blur="onInputBlur"
      @focus="onInputFocus"
      @keydown="emit('keydown', $event)" />
    <p class="absolute top-10 text-xs px-3" :class="helperClass">{{ helper }}</p>
  </div>
</template>

<style>
@layer tailwind {
  input {
    @apply px-3 py-2
  }
}
/* Avoid native search decorations conflicting with our own clear control. */
input[type='search']::-webkit-search-decoration,
input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-results-button,
input[type='search']::-webkit-search-results-decoration {
  -webkit-appearance: none;
}
</style>
